import type { ReactNode } from 'react';
import {
  Grid,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  FormHelperText,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { createElement, useMemo } from 'react';
import type { FormDescriptor, FormFieldDescriptor } from '@finance/contracts';
import { resolveTKey, translateRuntimeTKeyOrFallback } from '../../i18n/resolve-key';
import { useAppI18n } from '../../i18n/use-app-i18n';
import { formTokens } from './forms/form.tokens.ts';
import type { ResolvedFieldState } from '../../runtime/forms/field-state';
import { getFieldAdapter } from '../../runtime/forms/field-adapters';
import type { FieldAdapterProps } from '../../runtime/forms/field-adapter-registry';
import { ReadonlyValueAdapter } from './forms/ReadonlyValueAdapter';
import { MaskedValueAdapter } from './forms/MaskedValueAdapter';
import type { UseFormReturn } from 'react-hook-form';

export interface FormLayoutProps {
  descriptor: FormDescriptor;
  visibleFields: FormFieldDescriptor[];
  fieldStates: Map<string, ResolvedFieldState>;
  resolvedSections?: Map<string, boolean>;
  register: FieldAdapterProps['register'];
  control?: UseFormReturn['control'];
  formState: FieldAdapterProps['formState'];
  getValues?: UseFormReturn['getValues'];
  serverErrors?: Record<string, string> | null;
  pending: boolean;
  formValues?: Record<string, unknown>;
  renderFieldSupplement?: (
    field: FormFieldDescriptor,
    formValues: Record<string, unknown>,
  ) => ReactNode;
}

function layoutToGridSize(field: FormFieldDescriptor, sectionColumns?: 1 | 2 | 3 | 4) {
  const defaultMd = sectionColumns ? Math.floor(12 / sectionColumns) : 12;
  return {
    xs: field.layout?.xs ?? 12,
    sm: field.layout?.sm ?? field.layout?.xs ?? 12,
    md: field.layout?.md ?? field.layout?.sm ?? field.layout?.xs ?? defaultMd,
    lg: field.layout?.lg ?? field.layout?.md ?? field.layout?.sm ?? field.layout?.xs ?? defaultMd,
  };
}

interface RenderFieldContext {
  field: FormFieldDescriptor;
  state: ResolvedFieldState;
  register: FieldAdapterProps['register'];
  control: FieldAdapterProps['control'];
  formState: FieldAdapterProps['formState'];
  serverErrors: Record<string, string> | undefined;
  pending: boolean;
  t: FieldAdapterProps['t'];
  formValues: Record<string, unknown> | undefined;
  getValues: FieldAdapterProps['getValues'];
  mode: 'create' | 'edit' | 'view';
  describedBy?: string;
}

function renderField(ctx: RenderFieldContext) {
  const {
    field,
    state,
    register,
    control,
    formState,
    serverErrors,
    pending,
    t,
    formValues,
    getValues,
    mode,
    describedBy,
  } = ctx;

  // `requiredCondition` is resolved into state.required by the field-state
  // runtime. Adapters validate from the field descriptor, so materialize a
  // runtime required rule when a condition makes the field required. Without
  // this bridge a field can display the required marker yet submit empty.
  const hasRequiredRule = field.validation?.rules?.some(
    rule => String(rule.ruleId) === 'required',
  );
  const runtimeValidation =
    state.required && !hasRequiredRule
      ? {
          ...field.validation,
          rules: [
            ...(field.validation?.rules ?? []),
            { ruleId: 'required' as never, messageKey: 'validation.required' as never },
          ],
        }
      : field.validation;
  const adapterField: FormFieldDescriptor = {
    ...field,
    ...(field.helpTextKey ? { helpTextKey: undefined } : {}),
    ...(runtimeValidation ? { validation: runtimeValidation } : {}),
  };
  const rawServerError = serverErrors?.[field.name] ?? serverErrors?.[field.id];
  const serverError =
    (field.fieldType === 'collection' || field.fieldType === 'positionTransferLots') && rawServerError
      ? translateRuntimeTKeyOrFallback(t, rawServerError)
      : rawServerError;
  const adapterFormState: FieldAdapterProps['formState'] =
    (field.fieldType === 'collection' || field.fieldType === 'positionTransferLots') && rawServerError && serverError
      ? ({
          ...formState,
          errors: {
            ...formState.errors,
            [field.name]: {
              ...(formState.errors[field.name] ?? {}),
              type: formState.errors[field.name]?.type ?? 'server',
              message: serverError,
            },
          },
        } as FieldAdapterProps['formState'])
      : formState;
  const displayProps: FieldAdapterProps = {
    field: adapterField,
    state,
    register,
    control,
    formState: adapterFormState,
    serverError,
    pending,
    t,
    formValues,
    getValues,
    describedBy,
  };

  if (state.masked) return <MaskedValueAdapter {...displayProps} />;
  if (state.accessDenied) {
    return <Alert severity="warning">{t('common:states.forbidden.message')}</Alert>;
  }

  const adapter = getFieldAdapter(field.fieldType);
  if ((field.fieldType === 'collection' || field.fieldType === 'positionTransferLots') && adapter) return createElement(adapter.component, displayProps);

  if (mode === 'view' || state.readonly) return <ReadonlyValueAdapter {...displayProps} />;

  if (!adapter) {
    return (
      <Alert severity="error" variant="filled">
        {t('form:unknownFieldType', { fieldType: field.fieldType, fieldId: field.id })}
      </Alert>
    );
  }

  return createElement(adapter.component, displayProps);
}

export function FormLayout({
  descriptor,
  visibleFields,
  fieldStates,
  resolvedSections,
  register,
  control,
  formState,
  getValues,
  serverErrors,
  pending,
  formValues,
  renderFieldSupplement,
}: FormLayoutProps) {
  const { translator: t } = useAppI18n();
  const layoutFields = useMemo(
    () => visibleFields.filter(field => field.fieldType !== 'hidden'),
    [visibleFields],
  );
  const visibleFieldIds = useMemo(
    () => new Set(layoutFields.map(field => field.id)),
    [layoutFields],
  );
  const fieldById = useMemo(
    () => new Map(descriptor.fields.map(field => [field.id, field])),
    [descriptor.fields],
  );
  const sortedVisibleFields = useMemo(
    () => [...layoutFields].sort((left, right) => (left.layout?.order ?? 0) - (right.layout?.order ?? 0)),
    [layoutFields],
  );

  const fieldHelp = (field: FormFieldDescriptor) => {
    if (!field.helpTextKey) return undefined;
    return t(resolveTKey(field.helpTextKey));
  };

  const fieldNode = (field: FormFieldDescriptor, describedBy?: string) => {
    const state = fieldStates.get(field.name);
    if (!state) return null;
    return renderField({
      field,
      state,
      register,
      control,
      formState,
      serverErrors: serverErrors ?? undefined,
      pending,
      t,
      formValues,
      getValues,
      mode: descriptor.mode,
      describedBy,
    });
  };

  const gridForFields = (fields: FormFieldDescriptor[], columns?: 1 | 2 | 3 | 4) => (
    <Grid container spacing={formTokens.spacing.sectionInnerGap} sx={{ alignItems: 'flex-start' }}>
      {fields.map(field => {
        const helpText = fieldHelp(field);
        const helpId = helpText
          ? `${String(field.id).replace(/[^a-zA-Z0-9_-]/g, '-')}-help`
          : undefined;
        return (
          <Grid
            key={field.id}
            size={layoutToGridSize(field, columns)}
            data-field-id={field.id}
            data-field-name={field.name}
            sx={{ display: 'flex', minWidth: 0, alignItems: 'flex-start' }}
          >
            <Box
              role="group"
              aria-describedby={helpId}
              sx={{
                width: '100%',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {fieldNode(field, helpId)}
              {renderFieldSupplement?.(field, formValues ?? {})}
              {helpText && (
                <FormHelperText
                  id={helpId}
                  sx={{
                    mx: 0.5,
                    mt: 0.75,
                    lineHeight: 1.35,
                    color: 'text.secondary',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                  }}
                >
                  {helpText}
                </FormHelperText>
              )}
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );

  if (!descriptor.sections || descriptor.sections.length === 0) {
    return (
      <Box sx={{ mb: formTokens.spacing.sectionGap, minWidth: 0 }}>
        {gridForFields(sortedVisibleFields)}
      </Box>
    );
  }

  const sectionFieldIds = new Set(descriptor.sections.flatMap(section => section.fields));
  const unsectionedFields = sortedVisibleFields.filter(field => !sectionFieldIds.has(field.id));

  return (
    <Box sx={{ mb: formTokens.spacing.sectionGap, minWidth: 0 }}>
      {unsectionedFields.length > 0 && (
        <Box component="section" sx={{ mb: formTokens.spacing.sectionGap, minWidth: 0 }}>
          {gridForFields(unsectionedFields, 2)}
        </Box>
      )}

      {descriptor.sections.map(section => {
        const sectionVisible =
          resolvedSections?.get(section.id as string) ?? !section.visibilityCondition;
        if (sectionVisible === false) return null;

        const sectionFields = section.fields
          .map(id => fieldById.get(id))
          .filter((field): field is FormFieldDescriptor => Boolean(field && visibleFieldIds.has(field.id)))
          .sort((left, right) => (left.layout?.order ?? 0) - (right.layout?.order ?? 0));
        if (sectionFields.length === 0) return null;

        const sectionTitle = section.titleKey ? t(resolveTKey(section.titleKey)) : undefined;
        const sectionDescription = section.descriptionKey
          ? t(resolveTKey(section.descriptionKey))
          : undefined;
        const fields = gridForFields(sectionFields, section.layout?.columns);

        if (section.collapsible) {
          return (
            <Accordion key={section.id} defaultExpanded={!section.initiallyCollapsed}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minWidth: 0 }}>
                {sectionTitle && (
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
                  >
                    {sectionTitle}
                  </Typography>
                )}
              </AccordionSummary>
              <AccordionDetails sx={{ minWidth: 0 }}>
                {sectionDescription && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, overflowWrap: 'anywhere' }}
                  >
                    {sectionDescription}
                  </Typography>
                )}
                {fields}
              </AccordionDetails>
            </Accordion>
          );
        }

        return (
          <Box
            key={section.id}
            component="section"
            sx={{ mb: formTokens.spacing.sectionGap, minWidth: 0 }}
          >
            {(sectionTitle || sectionDescription) && (
              <Box
                sx={{
                  mb: 2.5,
                  pb: 1.5,
                  minWidth: 0,
                  borderBottom: theme => `1px solid ${theme.palette.divider}`,
                }}
              >
                {sectionTitle && (
                  <Typography
                    variant={formTokens.typography.sectionTitle}
                    component="h2"
                    sx={{ mb: sectionDescription ? 0.75 : 0, overflowWrap: 'anywhere' }}
                  >
                    {sectionTitle}
                  </Typography>
                )}
                {sectionDescription && (
                  <Typography
                    variant={formTokens.typography.sectionDescription}
                    color="text.secondary"
                    sx={{ overflowWrap: 'anywhere' }}
                  >
                    {sectionDescription}
                  </Typography>
                )}
              </Box>
            )}
            {fields}
          </Box>
        );
      })}
    </Box>
  );
}
